import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { usePrevious } from 'hooks';
import { truncateKeywordSpaces } from 'utils/utils';
import { Autocomplete } from 'components/FormFields';
import { partnersActions } from 'state/modules/partners';

const PartnersSelect = ({
  id,
  label,
  margin,
  variant,
  onChange,
  disabled,
  firstLoad,
  defaultField,
  customParams,
  searchBy,
  field: { name, value },
  form: { errors, touched, setFieldValue },
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const params = {
    page: 1,
    limit: 20,
    ...customParams,
  };

  const { partners, fetchingPartners } = useSelector((state) => state.partners);
  const prevvalue = usePrevious(customParams);
  const previd = usePrevious(value);

  const fetchPartners = useCallback(
    debounce((params, list) => {
      const newParams = {
        ...params,
      };
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      if (list) {
        return dispatch(partnersActions.fetchPartnersById(value, true));
      }
      dispatch(partnersActions.fetchPartners(newParams, list));
    }, 1000),
    []
  );

  useEffect(() => {
    if (firstLoad) {
      const _params = { ...params };
      if (defaultField !== 'id' && value) {
        _params[defaultField] = value;
      }
      if (searchBy && value) {
        _params[searchBy] = value;
      }

      fetchPartners(_params);
    }
    if (value && !defaultValue?.id) {
      dispatch(partnersActions.fetchPartnersById(value, true));
    }

    return () => {
      dispatch(partnersActions.resetPartnersData(value, true));
    };
  }, [firstLoad, fetchPartners,value]) // eslint-disable-line

  useEffect(() => {
    if (prevvalue && customParams && JSON.stringify(prevvalue) !== JSON.stringify(customParams)) {
      setFieldValue(name, '');
      fetchPartners({ ...params, ...customParams });
    }
  }, [customParams]) // eslint-disable-line

  useEffect(() => {
    if (previd !== value && (value || value === '' || !defaultValue)) {
      const defaultVal = partners.find((el) => el?.[defaultField] === parseInt(value));
      if (defaultVal) {
        if (onChange) {
          onChange(defaultVal);
        }
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }

    if (previd === value && partners.length > 0) {
      const defaultVal = partners.find((el) => el?.[defaultField] === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      }
    }
  }, [value, partners]) // eslint-disable-line

  useEffect(() => {
    if (partners && value && !defaultValue) {
      const defaultVal = partners.find((el) => el?.[defaultField] === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value === '') {
        setDefaultValue('');
      }
    }
  }, [partners]) // eslint-disable-line

  const handleFetchPartners = () => {
    if (partners.length === 0 && fetchingPartners !== 'LOADING' && !params?.keyword)
      dispatch(partnersActions.fetchPartners(params));
  };

  return (
    <Autocomplete
      id={id}
      name={name}
      label={label}
      margin={margin}
      fullwidth="true"
      variant={variant}
      options={partners}
      clearOnBlur={false}
      disabled={disabled}
      onHighlightChange={handleFetchPartners}
      value={defaultValue}
      getOptionLabel={(option) => option.name}
      onChange={(ev, val) => {
        if (onChange) {
          onChange(val);
        }
        setFieldValue(name, val?.[defaultField] || '');
      }}
      loading={fetchingPartners === 'LOADING' ? true : false}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option?.[defaultField] === value?.[defaultField]}
      onInputChange={(ev, val) => {
        const split = val?.split(' - ');
        fetchPartners({ ...params, keyword: split?.[0] || val });
      }}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

PartnersSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  onChange: PropTypes.func,
  defaultField: PropTypes.string,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

PartnersSelect.defaultProps = {
  required: true,
  field: {
    name: 'partnersSelect',
  },
  disabled: false,
  firstLoad: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  defaultField: 'id',
  label: 'Select...',
  variant: 'standard',
  id: 'partnersSelect',
};

export default PartnersSelect;
